<section class="rep" aria-label="SMS gateway log">
  <!-- What has loaded, in four figures. With only part of the period loaded
       they say so, so they never read as totals competing with the Dashboard. -->
  @if (status === "ready" && partial) {
    <p class="gl__scope">
      <span class="bk-sym" aria-hidden="true">info</span>
      <span>
        These figures cover the {{ formatCount(views.length) }} newest messages shown below, not all of
        {{ periodText }}. The Dashboard tab counts everything.
      </span>
    </p>
  }
  <div class="bk-stats gl__stats" [attr.aria-busy]="status === 'loading'">
    <div class="bk-stat">
      @if (status === "loading") {
        <span class="bk-skel stat-skel" aria-hidden="true"></span>
      } @else {
        <div class="bk-stat__value">{{ formatCount(messageCount) }}</div>
      }
      <div class="bk-stat__label">{{ partial ? "Messages shown" : "Messages in " + periodText }}</div>
    </div>

    <div class="bk-stat">
      @if (status === "loading") {
        <span class="bk-skel stat-skel" aria-hidden="true"></span>
      } @else {
        <div class="bk-stat__value">{{ formatCount(delivered) }}</div>
      }
      <div class="bk-stat__label">Delivered</div>
      @if (status === "ready" && messageCount) {
        <div class="stat-note">{{ rate }} of {{ partial ? "those shown" : "these messages" }}</div>
      }
    </div>

    <div class="bk-stat" [attr.data-tone]="failed > 0 ? 'bad' : null">
      @if (status === "loading") {
        <span class="bk-skel stat-skel" aria-hidden="true"></span>
      } @else {
        <div class="bk-stat__value">{{ formatCount(failed) }}</div>
      }
      <div class="bk-stat__label">Failed</div>
      @if (failed > 0 && outcome !== "failed") {
        <button type="button" class="bk-more stat-link stat-link--down" (click)="showFailures()">
          Show failures <span class="bk-sym" aria-hidden="true">arrow_downward</span>
        </button>
      }
    </div>

    <div class="bk-stat">
      @if (status === "loading") {
        <span class="bk-skel stat-skel" aria-hidden="true"></span>
      } @else if (paged) {
        <div class="bk-stat__value">{{ formatCount(pending) }}</div>
      } @else {
        <div class="bk-stat__value">{{ medianLatency }}</div>
      }
      @if (paged) {
        <div class="bk-stat__label">On the way</div>
        <div class="stat-note">No final receipt yet</div>
      } @else {
        <div class="bk-stat__label">Median time to delivery</div>
        @if (status === "ready" && pending > 0) {
          <div class="stat-note">{{ formatCount(pending) }} still on the way</div>
        }
      }
    </div>
  </div>

  <!-- Where failures cluster: a routing problem shows as one network or country -->
  @if (status === "ready" && failed > 0 && (byNetwork.length || byCountry.length)) {
    <section class="bk-panel gl__breakdown" aria-labelledby="gl-breakdown-title">
      <header class="gl__breakdown-head">
        <h2 class="bk-section-title" id="gl-breakdown-title">Where failures cluster</h2>
        <p class="gl__breakdown-note">
          @if (paged) {
            Across the newest {{ formatCount(sampleSize) }} messages in {{ periodText }}.
          } @else {
            Across the {{ formatCount(views.length) }} messages loaded.
          }
          One network or country with most of the failures usually points to routing, not the message.
        </p>
      </header>
      <div class="gl__groups">
        @for (list of [{ title: "By network", rows: byNetwork }, { title: "By country", rows: byCountry }]; track list.title) {
          <div class="gl__group">
            <h3 class="bk-eyebrow">{{ list.title }}</h3>
            <ul class="gl__bars">
              @for (row of list.rows; track row.name) {
                <li class="gl__bar">
                  <span class="gl__bar-name">{{ row.name }}</span>
                  <span class="gl__bar-figs">
                    <span class="is-bad">{{ formatCount(row.failed) }}</span>
                    of {{ formatCount(row.total) }} failed
                    <span class="gl__bar-share">{{ share(row) }}</span>
                  </span>
                  <span class="gl__track" aria-hidden="true">
                    <span class="gl__fill" [style.transform]="'scaleX(' + row.share + ')'"></span>
                  </span>
                </li>
              }
            </ul>
          </div>
        }
      </div>
    </section>
  }

  <div class="rep__toolbar gl__toolbar" #logToolbar>
    <app-search-input label="Search the gateway log"
      class="rep__search"
      [placeholder]="searchPlaceholder"
      (valueChange)="onSearch($event)"
    />

    <app-status-filter
      allLabel="All statuses"
      [options]="statusOptions"
      [selectedValue]="outcome"
      (selectionChange)="onOutcome($event)"
    />

    <span class="rep__spacer"></span>

    <button
      type="button"
      class="bk-btn bk-btn--ghost"
      [disabled]="status !== 'ready' || matchCount === 0 || exporting"
      [attr.aria-busy]="exporting || null"
      (click)="downloadCsv()"
    >
      <span class="bk-sym" aria-hidden="true">{{ exporting ? "hourglass_empty" : "download" }}</span>
      {{ exporting ? "Preparing…" : "Download log" }}
    </button>
  </div>

  @if (paged && term && !termDigits) {
    <p class="gl__search-hint">The log can be searched by phone number. Type digits to find a recipient.</p>
  }

  <!-- Always in the page, so screen readers announce each new notice. -->
  <p class="rep__notice" role="status" [attr.data-tone]="noticeTone" [class.bk-sr-only]="!notice">{{ notice }}</p>

  <!-- "Show failures" moves focus here once the filter applies. -->
  <div class="bk-panel rep__panel" tabindex="-1" #logPanel [class.is-paging]="paging" [attr.aria-busy]="paging || null">
    <data-table
      label="SMS gateway log"
      [seamless]="true"
      [loading]="status === 'loading'"
      [resizable]="false"
      [maxHeight]="'720px'"
      [skeletonColumns]="5"
    >
      @if (status === "error") {
        <app-empty-state
          icon="cloud_off"
          [message]="errorText.message"
          [hint]="errorText.hint"
          actionLabel="Try again"
          actionIcon="refresh"
          (action)="reload()"
        />
      } @else if (pageRows.length > 0) {
        <table class="bk-log">
          <thead>
            <tr>
              <th scope="col">Destination</th>
              <th scope="col" class="col-sender">Sender</th>
              <th scope="col" class="col-status">Status</th>
              <th scope="col" class="num col-when">Submitted</th>
              <th scope="col" class="col-actions"><span class="bk-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            @for (view of pageRows; track view.key; let i = $index) {
              <tr
                class="is-link"
                tabindex="0"
                [class.is-open]="expandedKey === view.key"
                (click)="toggle(view)"
                (keydown.enter)="toggleFromKeyboard($event, view)"
              >
                <td class="col-lead">
                  <span class="who">
                    <span class="who__main">{{ view.destination || "Unknown number" }}</span>
                    <span class="who__sub">
                      {{ view.network || "Network not reported" }}{{ view.country ? " · " + view.country : "" }}
                    </span>
                  </span>
                </td>
                <td class="quiet col-sender">{{ view.sender || "—" }}</td>
                <td class="col-status">
                  <span class="status-stack">
                    <app-status [text]="view.statusLabel" [status]="view.tone" />
                    @if (view.narration) {
                      <span class="status-stack__why">{{ view.narration }}</span>
                    }
                  </span>
                </td>
                <td class="num quiet col-when" [attr.title]="view.submittedExact || null">{{ view.submitted }}</td>
                <td class="col-actions">
                  <span class="bk-row-actions">
                    @if (view.messageId) {
                      <button
                        type="button"
                        class="bk-icon-btn"
                        [attr.aria-label]="copiedKey === view.key ? 'Message ID copied' : 'Copy message ID'"
                        [matTooltip]="copiedKey === view.key ? 'Copied' : 'Copy message ID'"
                        (click)="$event.stopPropagation(); copyId(view)"
                      >
                        <span class="bk-sym" aria-hidden="true">{{ copiedKey === view.key ? "check" : "content_copy" }}</span>
                      </button>
                    }
                    <button
                      type="button"
                      class="bk-icon-btn"
                      [attr.aria-expanded]="expandedKey === view.key"
                      [attr.aria-controls]="expandedKey === view.key ? 'gl-detail-' + i : null"
                      [attr.aria-label]="expandedKey === view.key ? 'Hide details' : 'Show details'"
                      (click)="$event.stopPropagation(); toggle(view)"
                    >
                      <span class="bk-sym row-go--toggle" aria-hidden="true">expand_more</span>
                    </button>
                  </span>
                </td>
              </tr>
              @if (expandedKey === view.key) {
                <tr class="gl__detail" [id]="'gl-detail-' + i">
                  <td colspan="5">
                    <div class="gl__detail-body">
                      @if (view.content) {
                        <p class="gl__content">{{ view.content }}</p>
                      }
                      <dl class="bk-kv gl__kv">
                        <dt>Submitted</dt>
                        <dd>{{ view.submittedExact || "—" }}</dd>
                        <dt>Delivered</dt>
                        <dd>{{ view.deliveredExact || "—" }}</dd>
                        <dt>Status</dt>
                        <dd>{{ view.row.deliveryStatus || "—" }}{{ view.narration ? " · " + view.narration : "" }}</dd>
                        <dt>Message ID</dt>
                        <dd>{{ view.messageId || "—" }}</dd>
                        <dt>Submit ID</dt>
                        <dd>{{ view.row.submitId || "—" }}</dd>
                        <dt>Receipt ID</dt>
                        <dd>{{ view.row.receiptId || "—" }}</dd>
                        <dt>Client ID</dt>
                        <dd>{{ view.row.clientId || "—" }}</dd>
                        <dt>Bulk reference</dt>
                        <dd>{{ view.row.bulkReferenceId || "—" }}</dd>
                        <dt>Network code</dt>
                        <dd>{{ view.row.mobileCountryCode || "—" }}</dd>
                      </dl>
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      } @else if (term && isFiltered) {
        <app-empty-state [query]="term" [filtered]="true" (clear)="clearFilters()" />
      } @else if (term) {
        <app-empty-state [query]="term" />
      } @else if (isFiltered) {
        <app-empty-state [filtered]="true" (clear)="clearFilters()" />
      } @else {
        <app-empty-state
          icon="api"
          [message]="'No API messages in ' + periodText + '.'"
          hint="Messages your systems send through the SMS API show here once the gateway logs them. Try a longer period."
        />
      }
    </data-table>
  </div>

  @if (status === "ready" && views.length > 0) {
    <div class="rep__foot">
      <div class="pager">
        <span class="pager__info" role="status">
          <strong>{{ rangeText }}</strong> of {{ formatCount(matchCount) }} {{ pagerNoun }}
        </span>
        <span class="pager__btns">
          <button
            type="button"
            class="bk-icon-btn"
            aria-label="Previous page"
            matTooltip="Previous page"
            [disabled]="page === 0 || paging"
            (click)="prevPage()"
          >
            <span class="bk-sym" aria-hidden="true">chevron_left</span>
          </button>
          <button
            type="button"
            class="bk-icon-btn"
            aria-label="Next page"
            matTooltip="Next page"
            [disabled]="page >= lastPage || paging"
            (click)="nextPage()"
          >
            <span class="bk-sym" aria-hidden="true">chevron_right</span>
          </button>
        </span>
      </div>

      @if (hasMore) {
        <button type="button" class="bk-btn bk-btn--ghost" [disabled]="loadingMore" (click)="loadMore()">
          <span class="bk-sym" aria-hidden="true">{{ loadingMore ? "hourglass_empty" : "history" }}</span>
          {{ loadingMore ? "Loading…" : "Load older messages" }}
        </button>
      }
    </div>
    @if (moreFailed) {
      <p class="rep__notice" role="alert" data-tone="bad">Older messages could not be loaded. Try again.</p>
    }
    @if (capped) {
      <p class="rep__notice" role="status">
        These are the newest {{ views.length }} messages, the most the gateway log can return for now. The
        Dashboard tab has the full history.
      </p>
    }
  }
</section>
